import { CSSObject } from '@emotion/react';
import * as React from 'react';
import Button from '../Button';

const buttonVariantStyles: Record<LoadingButtonProps['variant'], CSSObject> = {
  primary: {
    backgroundColor: '#0074cc',
    color: '#fff',
  },
  quaternary: {
    backgroundColor: '#a73f8e',
    color: '#fff',
  },
  secondary: {
    backgroundColor: '#fff',
    color: '#0074CC',
  },
  tertiary: {
    backgroundColor: '#fafbfc',
    color: '#4B575D',
  },
};

type LoadingButtonProps = {
  /** The content of the button. */
  children: React.ReactNode;

  /** Disables the button's interactivity */
  disabled: Parameters<typeof Button>[0]['disabled'];

  /** A unique identifier for the button */
  id?: string;

  /** Displays a spinner instead of the normal content. */
  isLoading: boolean;

  /** The callback, if any, to invoke when the button is clicked. */
  onClick?: Parameters<typeof Button>[0]['onClick'];

  /** The desired height and width of the button. */
  size: Parameters<typeof Button>[0]['size'];

  /** The default behavior of the button. */
  type: Parameters<typeof Button>[0]['type'];

  /** The aesthetic to apply to the button */
  variant: Exclude<Parameters<typeof Button>[0]['variant'], 'destructive'>;

  /** CSS class names */
  className?: string;

  /** Test ID */
  testId?: string;
};

/** A loading button is a type of **molecule** that combines the `button` atom and an `svg` element to indicate progress. */
const LoadingButton: React.FC<LoadingButtonProps> = ({
  children,
  className,
  disabled,
  id,
  isLoading,
  onClick,
  size,
  type,
  variant,
  testId,
}) => {
  return (
    <Button
      className={className}
      disabled={disabled || isLoading}
      id={id}
      onClick={onClick}
      size={size}
      type={type}
      variant={variant}
      data-testid={testId}
    >
      {isLoading ? (
        <svg
          fill="none"
          height="24"
          viewBox="0 0 24 24"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="12"
            cy="12"
            r="7"
            stroke={buttonVariantStyles[variant].color as string}
            strokeOpacity="0.5"
            strokeWidth="2"
          />
          <path
            d="M 12 5 A 7 7 0 0 1 19 12"
            stroke={buttonVariantStyles[variant].color as string}
            strokeWidth="2"
          >
            <animateTransform
              attributeName="transform"
              dur="1s"
              from="0 12 12"
              repeatCount="indefinite"
              to="360 12 12"
              type="rotate"
            />
          </path>
        </svg>
      ) : (
        children
      )}
    </Button>
  );
};

export default LoadingButton;
